import React, { useEffect, useState } from "react";
import { X, Download } from "lucide-react";
import { getPref, setPref } from "../lib/prefs";

/**
 * PWA install banner — shows once when the browser fires `beforeinstallprompt`
 * (Android Chrome / desktop Chrome / Edge). iOS doesn't expose the event;
 * instead we show a Safari-specific tip with the "Add to Home Screen" hint.
 *
 * Dismissed state is persisted in a cookie pref so the banner stays out of
 * the way after the user closes it.
 */
const PREF_KEY = "pwa_dismissed_v1";

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getPref(PREF_KEY) === true) return;
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari detection — no install event, just show a tip
    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isStandalone = window.navigator.standalone;
    if (isIos && !isStandalone) {
      setShowIos(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setPref(PREF_KEY, true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      dismiss();
    } else {
      setVisible(false);
    }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white border border-[#EAE6E1] shadow-[0_8px_32px_rgba(42,47,43,0.12)] px-4 py-3 flex items-center gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-full bg-[#D18E6315] flex items-center justify-center shrink-0">
        <Download className="w-5 h-5 text-[#D18E63]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        {showIos ? (
          <>
            <div className="text-sm font-semibold text-[#2A2F2B] leading-tight">Add UpTick to your Home Screen</div>
            <div className="text-xs text-[#6B7068] mt-0.5">
              Tap <span className="inline-block px-1 font-mono">⎙</span> Share, then <span className="font-semibold">Add to Home Screen</span>.
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-semibold text-[#2A2F2B] leading-tight">Install UpTick</div>
            <div className="text-xs text-[#6B7068] mt-0.5">Faster, works offline, native-feeling.</div>
          </>
        )}
      </div>
      {!showIos && (
        <button
          type="button"
          onClick={install}
          data-testid="pwa-install-button"
          className="px-4 py-2 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-xs font-medium transition-soft min-h-[40px]"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
        className="p-2 rounded-full hover:bg-[#EAE6E1]/60 transition-soft text-[#6B7068] min-h-[40px] min-w-[40px]"
      >
        <X className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
